import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
  Collapse,
  ListItemButton,
  Badge,
  Avatar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  PersonRemove as PersonRemoveIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Assessment as AssessmentIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  ExpandLess,
  ExpandMore,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  InsertChart as InsertChartIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config/api';
import logo from '../assets/di-logo.jpg';
import NotificationSystem from '../components/NotificationSystem';

const drawerWidth = 250;

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getAuthHeaders } = useAuth();

  useEffect(() => {
    checkIfManager();
  }, [user]);

  const checkIfManager = async () => {
    if (!user || !user.id) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/user-hierarchy/user-hierarchy/${user.id}`, {
        headers: getAuthHeaders()
      });
      // Check if user has direct reports
      const hasDirectReports = response.data?.directReportsCount > 0 || 
                               (response.data?.teamMembers && response.data.teamMembers.length > 0);
      setIsManager(hasDirectReports);
    } catch (error) {
      console.error('Error checking if user is manager:', error);
      setIsManager(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleManagementToggle = () => {
    setManagementOpen(!managementOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed. Please try again.');
    }
  };

  const baseAdminMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
    { text: 'Onboarding management', icon: <PersonAddIcon />, path: '/admin/onboarding' },
    { text: 'Existing Employee', icon: <PersonIcon />, path: '/admin/admin-existing', roles: ['admin'] },
    { text: 'Offboarding', icon: <PersonRemoveIcon />, path: '/admin/offboarding', roles: ['admin'] },
  ];

  // Add Manager Reviews only if user is a manager
  const adminMenuItems = isManager 
    ? [...baseAdminMenuItems, { text: 'Manager Reviews', icon: <AssessmentIcon />, path: '/admin/manager/reviews' }]
    : baseAdminMenuItems;

  const managementSubItems = [
    { text: 'Department Master', icon: <SecurityIcon />, path: '/admin/department-master', roles: ['admin'] },
    { text: 'User Management', icon: <GroupIcon />, path: '/admin/users', roles: ['admin'] },
    { text: 'Document Management', icon: <FolderIcon />, path: '/admin/documents', roles: ['admin'] },
    { text: 'User Hierarchy', icon: <GroupIcon />, path: '/admin/hierarchy', roles: ['admin'] },
    { text: 'Document Requirements', icon: <AssignmentIcon />, path: '/admin/document-requirements', roles: ['admin'] },
    { text: 'Survey Management', icon: <AssessmentIcon />, path: '/admin/surveys', roles: ['admin'] },
    { text: 'Grievance Management', icon: <AssessmentIcon />, path: '/admin/grievances', roles: ['admin'] },
    { text: 'POSH Management', icon: <AssessmentIcon />, path: '/admin/posh', roles: ['admin'] },
    { text: 'Access Provisioning', icon: <SecurityIcon />, path: '/admin/access-provisioning', roles: ['admin'] },
  ];

  // Reports sub menu (collapsible)
  const reportsSubItems = [
    { text: 'Employee Documents Report', icon: <InsertChartIcon />, path: '/admin/reports/employee-documents', roles: ['admin'] },
  ];

  // Filter menu items based on user role
  const getFilteredMenuItems = () => {
    return adminMenuItems.filter(item => {
      if (!item.roles) return true; // Show items without role restrictions
      return item.roles.includes(user.primaryRole);
    });
  };

  const getFilteredManagementItems = () => {
    return managementSubItems.filter(item => {
      if (!item.roles) return true; // Show items without role restrictions
      return item.roles.includes(user.primaryRole);
    });
  };

  const getFilteredReportItems = () => {
    return reportsSubItems.filter(item => {
      if (!item.roles) return true;
      return item.roles.includes(user.primaryRole);
    });
  };

  // Check if any management item is currently selected
  const isManagementItemSelected = () => {
    return managementSubItems.some(item => location.pathname === item.path);
  };

  const isReportsItemSelected = () => {
    return reportsSubItems.some(item => location.pathname === item.path);
  };

  if (!user) return <div>Loading...</div>;

  const filteredMenuItems = getFilteredMenuItems();
  const filteredManagementItems = getFilteredManagementItems();
  const filteredReportItems = getFilteredReportItems();

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          py: 3,
          px: 2,
          textAlign: 'center',
          bgcolor: 'transparent',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {!isMobile && (
          <>
          {/* Logo above the title */}
          <Box
          component="img"
          src={logo}
          alt="Company Logo"
          sx={{
            height: 60,       // reduced size
            width: 'auto',
            mb: 0.3,            // margin bottom
            objectFit: 'contain',
            maxWidth: '80%',   // ensure it doesn't overflow
          }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#fff', letterSpacing: 0.3, mb: 0.3,fontSize: '22.4px' }}
          >
           Dive-In Connect
          </Typography>
        </>
      )}

      <Typography
        variant="body2"
        sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}
      >
        {user.email}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}
      >
        {user.primaryRole || user.role}
      </Typography>
    </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      <List>
        {filteredMenuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
            }}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2,
              mb: 2,
              bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
              color: location.pathname === item.path ? 'primary.contrastText' : 'primary.contrastText',
              fontWeight: location.pathname === item.path ? 700 : 500,
              '&:hover': {
                bgcolor: 'primary.dark',
                color: 'primary.contrastText',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1" sx={{ fontSize: '16', fontWeight: 600 }}>
                  {item.text}
                </Typography>
              }
            />
          </ListItem>
        ))}

        {/* Management Dropdown */}
        {filteredManagementItems.length > 0 && (
          <>
            <ListItemButton
              onClick={handleManagementToggle}
              sx={{
                borderRadius: 2,
                mb: 2,
                bgcolor: isManagementItemSelected() ? 'primary.light' : 'transparent',
                color: isManagementItemSelected() ? 'primary.contrastText' : 'primary.contrastText',
                fontWeight: isManagementItemSelected() ? 700 : 500,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  color: 'primary.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontSize: '16', fontWeight: 600 }}>
                    Management
                  </Typography>
                }
              />
              {managementOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={managementOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {filteredManagementItems.map((item) => (
                  <ListItemButton
                    key={item.text}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    selected={location.pathname === item.path}
                    sx={{
                      pl: 4,
                      borderRadius: 2,
                      mb: 1,
                      ml: 2,
                      mr: 2,
                      bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
                      color: location.pathname === item.path ? 'primary.contrastText' : 'primary.contrastText',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '14', fontWeight: 600 }}>
                          {item.text}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Reports Dropdown */}
        {filteredReportItems.length > 0 && (
          <>
            <ListItemButton
              onClick={() => setReportsOpen(prev => !prev)}
              sx={{
                borderRadius: 2,
                mb: 2,
                bgcolor: isReportsItemSelected() ? 'primary.light' : 'transparent',
                color: isReportsItemSelected() ? 'primary.contrastText' : 'primary.contrastText',
                fontWeight: isReportsItemSelected() ? 700 : 500,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  color: 'primary.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <AssessmentIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" sx={{ fontSize: '16', fontWeight: 600 }}>
                    Reports
                  </Typography>
                }
              />
              {(reportsOpen || isReportsItemSelected()) ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>

            <Collapse in={reportsOpen || isReportsItemSelected()} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {filteredReportItems.map((item) => (
                  <ListItemButton
                    key={item.text}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setMobileOpen(false);
                    }}
                    selected={location.pathname === item.path}
                    sx={{
                      pl: 4,
                      borderRadius: 2,
                      mb: 1,
                      ml: 2,
                      mr: 2,
                      bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
                      color: location.pathname === item.path ? 'primary.contrastText' : 'primary.contrastText',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '14', fontWeight: 600 }}>
                          {item.text}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            mb: 2,
            bgcolor: 'transparent',
            color: 'primary.contrastText',
            fontWeight: 500,
            '&:hover': {
              bgcolor: 'error.main',
              color: 'error.contrastText',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body1" sx={{ fontSize: '16', fontWeight: 600 }}>
                Logout
              </Typography>
            }
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top AppBar with Notifications and Profile */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1, 
          bgcolor: '#f5f5f5',
          color: 'text.primary',
          boxShadow: 'none',
          height: 50,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          left: { sm: drawerWidth },
        }}
      >
        <Toolbar sx={{ justifyContent: 'flex-end', minHeight: '50px !important', px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            {/* Notification System */}
            <NotificationSystem />

            {/* User Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'grey.700',
                  color: 'white',
                  width: 30,
                  height: 30,
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {user.firstName && user.lastName 
                  ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                  : user.name ? user.name.charAt(0).toUpperCase() : 'U'
                }
              </Avatar>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}`
                  : user.name || 'User'
                }
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* AppBar for Mobile */}
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1, bgcolor: 'primary.main', top: 50 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Dive-In Connect
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              background: 'linear-gradient(180deg, #7e57c2 0%, #4a327d 100%)',
              borderRight: 'none',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '100vh',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: isMobile ? 8 : 4, // Better desktop spacing - not too tight
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout; 